import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import DSBox from '../../../design-system/DSBox';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import { DSTypography } from '../../../design-system/Typography';
import usePublicCollectionCodeQuery from '../hooks/usePublicCollectionCodeQuery';
import CollectionCodeError from './CollectionCodeError';
import CollectionCodeLoading from './CollectionCodeLoading';
import CollectionCodeGrid from './CollectionCodeGrid';

const PublicCollectionCodeView = () => {
  const { orderInfo } = useParams<{ orderInfo: string }>();

  // Parse orderInfo (format: "confirmationNumber-orderId")
  const { confirmationNumber, orderId } = useMemo(() => {
    if (!orderInfo) return { confirmationNumber: '', orderId: '' };

    const lastDashIndex = orderInfo.lastIndexOf('-');
    if (lastDashIndex === -1) {
      return { confirmationNumber: '', orderId: '' };
    }

    return {
      confirmationNumber: orderInfo.substring(0, lastDashIndex),
      orderId: orderInfo.substring(lastDashIndex + 1),
    };
  }, [orderInfo]);

  const { data, isLoading, error } = usePublicCollectionCodeQuery(
    confirmationNumber,
    orderId,
  );

  // Show initial loading state
  if (isLoading && !data) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#1E1E1E',
          color: '#FFFFFF',
        }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  // Show error if request failed
  if (error) {
    return (
      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#1E1E1E',
          color: '#FFFFFF',
        }}
      >
        <DSTypography variant="h4">
          Something went wrong. Please try again later.
        </DSTypography>
      </DSBox>
    );
  }

  if (data?.status === 'inProgress') {
    return <CollectionCodeLoading />;
  }

  if (data?.status === 'completed' && data.collections) {
    return <CollectionCodeGrid collections={data.collections} />;
  }
  return <CollectionCodeError />;
};

export default PublicCollectionCodeView;
